import React, { useMemo, useState } from 'react';
import {
    Row, Col, Form, Collapse, Checkbox
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import StyledSelect from '../CustomAntdComponents/StyledSelect';
import StyledInput from '../CustomAntdComponents/StyledInput';
import {
    allowedAgedRange,
    genderOptions,
    titleOptions,
    validateDOB,
    validateNoFutureDate,
    validatePassportExpiration
} from './helper';
import { useI18nContext } from '../../utils/I18n';
import translations from '../../screens/PassengerDetails/translations';
import {StyledParagraph, StyledTitle} from "../CustomAntdComponents/Typography";
import {useFlightSearchStore} from "../../store/flightSearch/flightSearchStore";
import moment from "moment";
import { getCountries } from 'country-state-picker';
import {
    CheckBoxLabel, CollapseWrapper, InfoWrapper, 
    PrefillSectionWrapper, PrefillHeader, PrefillActions,
    StyledSavedProfileButton, StyledUploadButton,
    ProfileMenuItemWrapper, ProfileAvatar, ProfileInfo, ProfileName, ProfileDetails,
    SelectedProfileCard
} from "./styles";
import {useStore} from "../../store/global/appStore";
import {validateNameRequired, validatePassportNumberRequired, validateRequired} from "../../utils/helpers";
import CustomDatePicker from "../CustomDatePicker";
import CustomDateInput from '../CustomDateInput';
import { Upload, message, Button, Dropdown } from 'antd';
import {ReactComponent as BulbIcon} from '../../assets/images/bulb-filled-icon.svg';
import { CameraOutlined, DownOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../store/auth/authStore';
import { Pen2 } from '@solar-icons/react';

 

const { Panel } = Collapse;

type Props = {
    field: any,
    passengerType: string,
    fieldName?: string,
}
function FlightPassengerForm({
                           field, passengerType, fieldName
                       }: Props) {
    const {
        state: { t },
    } = useI18nContext();

    const { airlines } = useStore();

    const airlinesOptions = useMemo(() => airlines.map((airline: Airline) => ({
        label: airline.Name,
        value: airline.Code,
    })), [airlines]);

    const {
        formData: {
            isDomestic
        },
    } = useFlightSearchStore();
    const countryOptions = useMemo(() => {
        const countries = getCountries().map(({ name, code }: any) => ({
            label: name,
            value: code,
        }));

        const priorities = [
            {
                label: "Nigeria",
                value: "ng",
            },
            {
                label: "Kenya",
                value: "ke",
            }
        ]
        const others = countries.filter((c) => c.label !== "Kenya" && c.label !== "Nigeria");

        return [
            ...priorities,
            ...others
        ];
    }, []);

    const isAdult = passengerType === 'ADT';
    const form = Form.useFormInstance();

    const handleOCR = (file: any) => {
        message.loading({ content: 'Extracting data...', key: 'ocr' });
        
        // Simulate OCR API call
        setTimeout(() => {
            const mockData = {
                firstName: "JOHN",
                lastName: "DOE",
                dob: moment("1990-01-01"),
                passportNumber: "A12345678",
                issuanceCountry: "ng",
                issuanceDate: moment("2020-01-01"),
                passportExpiration: moment("2030-01-01"),
                gender: "Male",
                title: "Mr"
            };

            const currentValues = form.getFieldsValue();
            const arrayName = fieldName || 'adults';
            const passengers = [...(currentValues[arrayName] || [])];
            
            if (passengers[field.name]) {
                passengers[field.name] = {
                    ...passengers[field.name],
                    ...mockData
                };
            } else {
                passengers[field.name] = mockData;
            }
            form.setFieldsValue({ [arrayName]: passengers });
            message.success({ content: 'Passport data extracted!', key: 'ocr' });
        }, 2000);

        return false; // Prevent actual upload
    };

    const { isLoggedIn } = useAuthStore();
    const [selectedProfile, setSelectedProfile] = useState<any>(null);

    const mockProfiles = [
      { label: 'Raymond Reddington', value: '1', data: { firstName: 'Raymond', lastName: 'Reddington', dob: moment('1961-01-12'), title: 'Mr', gender: 'Male', passportNumber: 'ABC123456', issuanceCountry: 'ke', email: 'raymond@gmail.com' } },
      { label: 'John Snow', value: '2', data: { firstName: 'John', lastName: 'Snow', dob: moment('1961-01-12'), title: 'Mr', gender: 'Male', email: 'johnsnow@gmail.com' } },
      { label: 'Wanjiku Thomas', value: '3', data: { firstName: 'Wanjiku', lastName: 'Thomas', dob: moment('1961-01-12'), title: 'Ms', gender: 'Female', email: 'moses@gmail.com' } },
    ];

    const handleProfileSelect = (value: string) => {
      const profile = mockProfiles.find(p => p.value === value);
      if (profile) {
        setSelectedProfile(profile);
        const currentValues = form.getFieldsValue();
        const arrayName = fieldName || 'adults';
        const passengers = [...(currentValues[arrayName] || [])];
        
        if (passengers[field.name]) {
            passengers[field.name] = {
                ...passengers[field.name],
                ...profile.data
            };
        } else {
            passengers[field.name] = profile.data;
        }
        form.setFieldsValue({ [arrayName]: passengers });
      }
    };

    const profileMenu = {
      items: mockProfiles.map(p => ({
        key: p.value,
        label: (
          <ProfileMenuItemWrapper>
            <ProfileAvatar>
              {p.data.firstName[0]}{p.data.lastName[0]}
            </ProfileAvatar>
            <ProfileInfo>
              <ProfileName>
                  {p.data.firstName} {p.data.lastName} {(p.data as any).primary && <span>(Primary traveller)</span>}
              </ProfileName>
              <ProfileDetails>{p.data.email} • {p.data.dob.format('MMM DD, YYYY')}</ProfileDetails>
            </ProfileInfo>
          </ProfileMenuItemWrapper>
        )
      })),
      onClick: ({ key }: any) => handleProfileSelect(key)
    };

    return (
        <Row gutter={16}>
            <Col span={24}>
                <div style={{ marginBottom: "20px" }}>
                    {selectedProfile ? (
                        <SelectedProfileCard>
                            <ProfileMenuItemWrapper style={{ padding: 0 }}>
                                <ProfileAvatar>
                                    {selectedProfile.data.firstName[0]}{selectedProfile.data.lastName[0]}
                                </ProfileAvatar>
                                <ProfileInfo>
                                    <ProfileName>
                                        {selectedProfile.data.firstName} {selectedProfile.data.lastName} {(selectedProfile.data as any).primary && <span>(Primary traveller)</span>}
                                    </ProfileName>
                                    <ProfileDetails>{selectedProfile.data.email} • {selectedProfile.data.dob.format('MMM DD, YYYY')}</ProfileDetails>
                                </ProfileInfo>
                            </ProfileMenuItemWrapper>
                            <Button type="text" icon={<Pen2 size={24} color="#6B7280" />} onClick={() => setSelectedProfile(null)} />
                        </SelectedProfileCard>
                    ) : (
                        <PrefillSectionWrapper>
                            <PrefillHeader>
                                <BulbIcon />
                                <StyledTitle level={4}>Pre-fill your details</StyledTitle>
                            </PrefillHeader>
                            <StyledParagraph size="default" style={{ color: '#6B7280', fontSize: '13px' }}>
                                {isLoggedIn 
                                    ? "Upload your passport data page or use your saved profile and we will pre-fill your data for you"
                                    : "Upload your passport data page and we will pre-fill your data for you"}
                            </StyledParagraph>
                            <PrefillActions>
                                {isLoggedIn && (
                                    <Dropdown menu={profileMenu} trigger={['click']} placement="bottomLeft">
                                        <StyledSavedProfileButton shape="round">
                                            Use saved profile <DownOutlined style={{ fontSize: '12px' }} />
                                        </StyledSavedProfileButton>
                                    </Dropdown>
                                )}
                                <Upload showUploadList={false} beforeUpload={handleOCR} accept="image/*">
                                    <StyledUploadButton shape="round">
                                        Upload passport
                                    </StyledUploadButton>
                                </Upload>
                            </PrefillActions>
                        </PrefillSectionWrapper>
                    )}
                </div>
            </Col>

            <Col span={12}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'title']}
                    fieldKey={[field.key, 'title']}
                    rules={[{ required: true, message: t(translations).validations.title }]}
                >
                    <StyledSelect options={titleOptions} placeholder={t(translations).input.title} />
                </Form.Item>
            </Col>

             <Col span={12}>
               <Form.Item
                 {...field}
                 name={[field.name, 'gender']}
                 fieldKey={[field.key, 'gender']}
                 rules={[{ required: true, message: 'Please select gender' }]}
               >
                 <StyledSelect options={genderOptions} placeholder="Gender" />
               </Form.Item>
             </Col>

            <Col span={24}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'firstName']}
                    fieldKey={[field.key, 'firstName']}
                    rules={[{ validator: validateNameRequired }]}
                >
                    <StyledInput placeholder={t(translations).input.firstName} />
                </Form.Item>
            </Col>

            <Col span={24}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'lastName']}
                    fieldKey={[field.key, 'lastName']}
                    rules={[{ validator: validateNameRequired }]}
                >
                    <StyledInput placeholder={t(translations).input.lastName} />
                </Form.Item>
            </Col>

            <Col span={24}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'dob']}
                    fieldKey={[field.key, 'dob']}
                    rules={[
                        {
                            validator: (_, value: any) => {
                                return validateDOB(value, passengerType);
                            }
                        }
                    ]}
                >
                    {/* <CustomDatePicker label={t(translations).input.dob} onChange={(val) => val } /> */}
                    <CustomDateInput label={t(translations).input.dob} onChange={(val) => val } />
                </Form.Item>
            </Col>

            {
                !isDomestic && (
                    <>
                        <Col span={24}>
                            <div style={{ marginBottom: "20px"}}>
                                <StyledTitle level={4}>
                                    Passport details
                                </StyledTitle>
                            </div>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'passportNumber']}
                                fieldKey={[field.key, 'passportNumber']}
                                rules={[
                                    { validator: validatePassportNumberRequired },
                                ]}
                            >
                                <StyledInput placeholder={t(translations).input.passportNumber} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'issuanceCountry']}
                                fieldKey={[field.key, 'issuanceCountry']}
                                rules={[{ required: true, message: t(translations).validations.issuanceCountry }]}
                            >
                                <StyledSelect
                                    showSearch
                                    options={countryOptions}
                                    placeholder={t(translations).input.issuanceCountry}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'issuanceDate']}
                                rules={[
                                    {
                                        validator: (_, value: any) => {
                                            return validateNoFutureDate(value);
                                        }
                                    }
                                ]}
                            >
                                <CustomDateInput label={t(translations).input.passportIssuanceDate} onChange={(val) => val } />
                                {/* <CustomDatePicker label={t(translations).input.passportIssuanceDate} onChange={(val) => val } /> */}
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'passportExpiration']}
                                rules={[
                                    {
                                        validator: (_, value: any) => {
                                            return validatePassportExpiration(value);
                                        }
                                    }
                                ]}
                            >
                                <CustomDateInput label={t(translations).input.passportExpiration} onChange={(val) => val } startYearIndex={30} />
                            </Form.Item>
                        </Col>
                    </>
                )
            }

            <Col span={24}>
                <div style={{ marginBottom: "20px"}}>
                    <StyledTitle level={4}>
                        Additional information <span>(optional)</span>
                    </StyledTitle>
                </div>

                <CollapseWrapper>
                    <Collapse>
                        <Panel header="Frequent flyer? (optional)" key="1">
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'frequentFlyerAirline']}
                                fieldKey={[field.key, 'frequentFlyerAirline']}
                            >
                                <StyledSelect
                                    showSearch
                                    allowClear
                                    options={airlinesOptions}
                                    placeholder={t(translations).input.frequentFlyerAirline}
                                />
                            </Form.Item>

                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'frequentFlyerNumber']}
                                fieldKey={[field.key, 'frequentFlyerNumber']}
                            >
                                <StyledInput placeholder={t(translations).input.frequentFlyerNumber} />
                            </Form.Item>
                        </Panel>
                    </Collapse>
                </CollapseWrapper>
            </Col>

            {
                (field.key === 0  && isAdult)&& (
                    <Col span={24}>
                        <Form.Item
                            /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...field}
                            valuePropName="checked"
                            name={[field.name, 'savePassengerData']}
                            fieldKey={[field.key, 'savePassengerData']}
                        >
                            <Checkbox>
                                <CheckBoxLabel>
                                    Save my data for future purposes
                                </CheckBoxLabel>
                            </Checkbox>
                        </Form.Item>
                    </Col>
                )
            }
        </Row>
    );
}

export default FlightPassengerForm;
